import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { examService, examError } from "../api/services/exams";
import { isCanceled } from "../api/client";

const initial = { snapshot: null, busy: false, error: "", completed: false, closed: false, uncertain: false };
const isClosed = (error) => error.status === 423 && error.data?.message === "Exam Finished";
export function examReducer(state, action) {
  switch (action.type) {
    case "snapshot": return { ...state, snapshot: action.data, uncertain: false };
    case "busy": return { ...state, busy: action.value };
    case "error": return { ...state, error: action.message, uncertain: action.uncertain };
    case "clear": return { ...state, error: "" };
    case "complete": return { ...state, completed: true, error: "" };
    case "closed": return { ...state, closed: true, snapshot: null, error: action.message, uncertain: false };
    case "reset": return initial;
    default: return state;
  }
}

export default function useExam(id) {
  const [state, dispatch] = useReducer(examReducer, initial);
  const [now, setNow] = useState(Date.now());
  const stateRef = useRef(state);
  const request = useRef(null);
  const expiryHandled = useRef(new Set());
  const refreshPending = useRef(false);
  useEffect(() => { stateRef.current = state; });
  const action = useCallback(async (type, value) => {
    if (request.current || stateRef.current.completed || stateRef.current.closed) {
      if (type === "conf") refreshPending.current = true;
      return;
    }
    const controller = new AbortController();
    request.current = controller;
    dispatch({ type: "busy", value: true }); dispatch({ type: "clear" });
    const snapshot = (data) => {
      if (!data || !Array.isArray(data.u_q_list) || !Number.isFinite(Number(data.active_question_number))) {
        throw new Error("پاسخ آزمون معتبر نیست. همگام‌سازی را دوباره انجام دهید.");
      }
      if (controller.signal.aborted) return;
      dispatch({ type: "snapshot", data });
    };
    try {
      if (type === "answer") {
        await examService.answer(id, stateRef.current.snapshot.active_question_number, value, controller.signal);
        snapshot(await examService.flow(id, "conf", undefined, controller.signal));
      } else {
        const data = await examService.flow(id, type, type === "return" ? { aqn: value } : undefined, controller.signal);
        if (data?.success === false) throw new Error(data.message || "عملیات آزمون انجام نشد.");
        if (controller.signal.aborted) return;
        if (type === "finish") {
          if (data?.success !== true) throw new Error("پایان آزمون تأیید نشد. دوباره همگام‌سازی کنید.");
          dispatch({ type: "complete" });
        } else snapshot(data);
      }
      if (refreshPending.current && type !== "finish") {
        refreshPending.current = false;
        snapshot(await examService.flow(id, "conf", undefined, controller.signal));
      }
    } catch (error) {
      if (controller.signal.aborted || isCanceled(error)) return;
      if (isClosed(error)) {
        dispatch({ type: "closed", message: examError(error) });
      } else {
        dispatch({ type: "error", message: examError(error), uncertain: true });
        if (type !== "conf" && error.status !== 401) {
          try { snapshot(await examService.flow(id, "conf", undefined, controller.signal)); }
          catch (failure) {
            if (!controller.signal.aborted && !isCanceled(failure)) {
              dispatch({ type: isClosed(failure) ? "closed" : "error", message: examError(failure), uncertain: true });
            }
          }
        }
      }
    } finally {
      if (!controller.signal.aborted) dispatch({ type: "busy", value: false });
      if (request.current === controller) request.current = null;
    }
  }, [id]);

  useEffect(() => {
    stateRef.current = initial;
    dispatch({ type: "reset" });
    expiryHandled.current.clear();
    action("conf");
    const reconnect = () => action("conf");
    window.addEventListener("online", reconnect);
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      request.current?.abort(); request.current = null;
      window.removeEventListener("online", reconnect); clearInterval(interval);
    };
  }, [action]);
  const questionDeadline = Date.parse(state.snapshot?.q_time);
  const examDeadline = Date.parse(state.snapshot?.e_time);
  useEffect(() => {
    if (state.busy || state.completed || !state.snapshot) return;
    const type = now >= examDeadline ? "finish" : now >= questionDeadline ? "conf" : null;
    const key = type + ":" + (type === "finish" ? examDeadline : questionDeadline);
    if (type && !expiryHandled.current.has(key)) {
      expiryHandled.current.add(key); action(type);
    }
  }, [now, examDeadline, questionDeadline, state.busy, state.completed, state.snapshot, action]);
  return { ...state, action,
    questionSeconds: Math.max(0, Math.ceil((questionDeadline - now) / 1000)) || 0,
    examSeconds: Math.max(0, Math.ceil((examDeadline - now) / 1000)) || 0 };
}
